/**
 * Kunde-Ebene Risikoprofil — the SINGLE SOURCE OF TRUTH for the generelles Risikoprofil
 * (Firma/Privatperson) and the Basissparten-Checkliste (Soll) on the Kunde level
 * (P2 Placement Slice 1; Detailkonzept Ausschreibung V3.0 Ch 3, 4, 6.1, 7.1, 9).
 *
 * MODELING DECISION (slice-1 design): a Kunde-Risikoprofil is a typed spine
 * (`customer_risk_profile`) + ONE `attributes_json` holding every atomic field,
 * validated against the code-defined catalog below. The catalog-as-code idiom mirrors
 * permission.ts / visibility.ts / field-permission.ts: adding a field needs no migration.
 *
 * Three kinds of field, only the FIRST is stored in `attributes_json`:
 *   - STORED (this catalog): the ~65 atomar aufgeschluesselten Felder (40 Firma + 25
 *     Privatperson) that the broker edits per customer.
 *   - MIRROR (read-only, from `firma`): Rechtsform, Gruendungsjahr, NOGA-Code,
 *     Zweckbeschreibung — never duplicated into JSON (Ch 3.1 "bereits vorhanden" / Zefix).
 *   - DERIVED (read-only, computed): M4 counts (Policen/Sparten) now, M8 KPIs later —
 *     never duplicated into JSON (Ch 3.1 "M4/M8").
 *
 * The two besonders schuetzenswerten Attribute (Statusflag Gesundheitspruefung,
 * Einkommens-Bandbreite) are DEDICATED COLUMNS on `customer_risk_profile` (never in
 * JSON), so Datenschutz-Gating (Ch 2.4) maps 1:1 to a column and can never leak through a
 * generic JSON export. Their keys are RISIKOPROFIL_GATED_FIELD_KEYS; they are Privatperson
 * only.
 *
 * GwG attributes (Laenderrisiko, PEP, UBO, Transaktionsverhalten) are deliberately ABSENT
 * (Ch 3.1 end: they stay in module M5).
 *
 * [Source: docs/product/2026-09-24_Brokly_Detailkonzept_Ausschreibung_V3.0.txt Ch 3/4/6.1]
 * [Source: docs/product/p2-slices/slice-1-kunde-ebene-design.md §3/§4]
 * [Source: packages/shared/src/permission.ts + visibility.ts (catalog-as-code precedent)]
 */

import type { SubjectKind } from './policy';

// ── Profil-Typ (Firma / Privatperson) ─────────────────────────────────────────

export const RISIKOPROFIL_TYP = {
  FIRMA: 'firma',
  PRIVATPERSON: 'privatperson',
} as const;
export type RisikoprofilTyp = (typeof RISIKOPROFIL_TYP)[keyof typeof RISIKOPROFIL_TYP];
export const RISIKOPROFIL_TYP_VALUES: readonly RisikoprofilTyp[] = [
  RISIKOPROFIL_TYP.FIRMA,
  RISIKOPROFIL_TYP.PRIVATPERSON,
];
export const RISIKOPROFIL_TYP_LABELS: Record<RisikoprofilTyp, string> = {
  [RISIKOPROFIL_TYP.FIRMA]: 'Firmenkunde',
  [RISIKOPROFIL_TYP.PRIVATPERSON]: 'Privatperson',
};

// ── Kategorien (one card per Kategorie in the UI) ──────────────────────────────

export const RISIKOPROFIL_KATEGORIE = {
  // Firma (Ch 3.1)
  UNTERNEHMENSSTRUKTUR: 'unternehmensstruktur',
  GROESSE_FINANZEN: 'groesse_finanzen',
  BRANCHE_TAETIGKEIT: 'branche_taetigkeit',
  BETRIEBSRISIKO: 'betriebsrisiko',
  SICHERHEIT_PRAEVENTION: 'sicherheit_praevention',
  GEOGRAFISCHES_RISIKO: 'geografisches_risiko',
  PORTFOLIO_KENNZAHLEN: 'portfolio_kennzahlen',
  // Privatperson (Ch 3.2)
  DEMOGRAFIE_HAUSHALT: 'demografie_haushalt',
  WOHNSITUATION: 'wohnsituation',
  ERWERBSSITUATION: 'erwerbssituation',
  VERMOEGEN_OBJEKT: 'vermoegen_objekt',
  RISIKOAFFINITAET_LEBENSSTIL: 'risikoaffinitaet_lebensstil',
  VORSORGE: 'vorsorge',
  BESONDERE_PERSONENDATEN: 'besondere_personendaten',
  // shared across both Typen
  SCHADEN_VERTRAGSHISTORIE: 'schaden_vertragshistorie',
} as const;
export type RisikoprofilKategorie =
  (typeof RISIKOPROFIL_KATEGORIE)[keyof typeof RISIKOPROFIL_KATEGORIE];
export const RISIKOPROFIL_KATEGORIE_LABELS: Record<RisikoprofilKategorie, string> = {
  [RISIKOPROFIL_KATEGORIE.UNTERNEHMENSSTRUKTUR]: 'Unternehmensstruktur',
  [RISIKOPROFIL_KATEGORIE.GROESSE_FINANZEN]: 'Groesse & Finanzkennzahlen',
  [RISIKOPROFIL_KATEGORIE.BRANCHE_TAETIGKEIT]: 'Branche & Geschaeftstaetigkeit',
  [RISIKOPROFIL_KATEGORIE.BETRIEBSRISIKO]: 'Betriebsrisiko-Indikatoren',
  [RISIKOPROFIL_KATEGORIE.SICHERHEIT_PRAEVENTION]: 'Sicherheits- & Praeventionsmassnahmen',
  [RISIKOPROFIL_KATEGORIE.GEOGRAFISCHES_RISIKO]: 'Geografisches Risiko',
  [RISIKOPROFIL_KATEGORIE.PORTFOLIO_KENNZAHLEN]: 'Portfolio- & Vertragskennzahlen',
  [RISIKOPROFIL_KATEGORIE.DEMOGRAFIE_HAUSHALT]: 'Demografie & Haushalt',
  [RISIKOPROFIL_KATEGORIE.WOHNSITUATION]: 'Wohnsituation',
  [RISIKOPROFIL_KATEGORIE.ERWERBSSITUATION]: 'Erwerbssituation',
  [RISIKOPROFIL_KATEGORIE.VERMOEGEN_OBJEKT]: 'Vermoegens- und Objektsituation',
  [RISIKOPROFIL_KATEGORIE.RISIKOAFFINITAET_LEBENSSTIL]: 'Risikoaffinitaet & Lebensstil',
  [RISIKOPROFIL_KATEGORIE.VORSORGE]: 'Vorsorgesituation',
  [RISIKOPROFIL_KATEGORIE.BESONDERE_PERSONENDATEN]: 'Besondere Personendaten',
  [RISIKOPROFIL_KATEGORIE.SCHADEN_VERTRAGSHISTORIE]: 'Schaden- und Vertragshistorie',
};

/** Ordered Kategorie list per Typ (the card order in the UI). */
export const RISIKOPROFIL_KATEGORIEN_JE_TYP: Record<RisikoprofilTyp, readonly RisikoprofilKategorie[]> = {
  [RISIKOPROFIL_TYP.FIRMA]: [
    RISIKOPROFIL_KATEGORIE.UNTERNEHMENSSTRUKTUR,
    RISIKOPROFIL_KATEGORIE.GROESSE_FINANZEN,
    RISIKOPROFIL_KATEGORIE.BRANCHE_TAETIGKEIT,
    RISIKOPROFIL_KATEGORIE.BETRIEBSRISIKO,
    RISIKOPROFIL_KATEGORIE.SCHADEN_VERTRAGSHISTORIE,
    RISIKOPROFIL_KATEGORIE.SICHERHEIT_PRAEVENTION,
    RISIKOPROFIL_KATEGORIE.GEOGRAFISCHES_RISIKO,
    RISIKOPROFIL_KATEGORIE.PORTFOLIO_KENNZAHLEN,
  ],
  [RISIKOPROFIL_TYP.PRIVATPERSON]: [
    RISIKOPROFIL_KATEGORIE.DEMOGRAFIE_HAUSHALT,
    RISIKOPROFIL_KATEGORIE.WOHNSITUATION,
    RISIKOPROFIL_KATEGORIE.ERWERBSSITUATION,
    RISIKOPROFIL_KATEGORIE.VERMOEGEN_OBJEKT,
    RISIKOPROFIL_KATEGORIE.RISIKOAFFINITAET_LEBENSSTIL,
    RISIKOPROFIL_KATEGORIE.VORSORGE,
    RISIKOPROFIL_KATEGORIE.SCHADEN_VERTRAGSHISTORIE,
    RISIKOPROFIL_KATEGORIE.BESONDERE_PERSONENDATEN,
  ],
};

// ── Gated columns (Privatperson only — Ch 2.4 Datenschutz-Gating) ──────────────

export const GESUNDHEITSPRUEFUNG_STATUS = {
  AUSSTEHEND: 'ausstehend',
  ABGESCHLOSSEN: 'abgeschlossen',
} as const;
export type GesundheitspruefungStatus =
  (typeof GESUNDHEITSPRUEFUNG_STATUS)[keyof typeof GESUNDHEITSPRUEFUNG_STATUS];
export const GESUNDHEITSPRUEFUNG_STATUS_VALUES: readonly GesundheitspruefungStatus[] = [
  GESUNDHEITSPRUEFUNG_STATUS.AUSSTEHEND,
  GESUNDHEITSPRUEFUNG_STATUS.ABGESCHLOSSEN,
];
export const GESUNDHEITSPRUEFUNG_STATUS_LABELS: Record<GesundheitspruefungStatus, string> = {
  [GESUNDHEITSPRUEFUNG_STATUS.AUSSTEHEND]: 'Ausstehend',
  [GESUNDHEITSPRUEFUNG_STATUS.ABGESCHLOSSEN]: 'Abgeschlossen',
};

/**
 * Einkommens-Bandbreite buckets (Privatperson gated column). The bucket values are a
 * PROPOSAL flagged for Stefan (Ch 14 open point) — cheap to change (code catalog, no
 * migration shape change beyond the CHECK list).
 */
export const EINKOMMENS_BANDBREITE = {
  BIS_60K: 'einkommen_bis_60k',
  VON_60K_100K: 'einkommen_60k_100k',
  VON_100K_150K: 'einkommen_100k_150k',
  VON_150K_250K: 'einkommen_150k_250k',
  UEBER_250K: 'einkommen_ueber_250k',
} as const;
export type EinkommensBandbreite =
  (typeof EINKOMMENS_BANDBREITE)[keyof typeof EINKOMMENS_BANDBREITE];
export const EINKOMMENS_BANDBREITE_VALUES: readonly EinkommensBandbreite[] = [
  EINKOMMENS_BANDBREITE.BIS_60K,
  EINKOMMENS_BANDBREITE.VON_60K_100K,
  EINKOMMENS_BANDBREITE.VON_100K_150K,
  EINKOMMENS_BANDBREITE.VON_150K_250K,
  EINKOMMENS_BANDBREITE.UEBER_250K,
];
export const EINKOMMENS_BANDBREITE_LABELS: Record<EinkommensBandbreite, string> = {
  [EINKOMMENS_BANDBREITE.BIS_60K]: 'bis CHF 60k',
  [EINKOMMENS_BANDBREITE.VON_60K_100K]: 'CHF 60k bis 100k',
  [EINKOMMENS_BANDBREITE.VON_100K_150K]: 'CHF 100k bis 150k',
  [EINKOMMENS_BANDBREITE.VON_150K_250K]: 'CHF 150k bis 250k',
  [EINKOMMENS_BANDBREITE.UEBER_250K]: 'ueber CHF 250k',
};

/**
 * Umsatz-/Bilanz-/Eigenkapital-Bandbreite buckets (Firma, STORED json). Bucket values are a
 * PROPOSAL flagged for Stefan (Ch 14 open point). Shared by jahresumsatz_bandbreite,
 * bilanzsumme_bandbreite and eigenkapital_bandbreite.
 */
export const UMSATZ_BANDBREITE = {
  BIS_500K: 'umsatz_bis_500k',
  VON_500K_2M: 'umsatz_500k_2m',
  VON_2M_10M: 'umsatz_2m_10m',
  VON_10M_50M: 'umsatz_10m_50m',
  UEBER_50M: 'umsatz_ueber_50m',
} as const;
export type UmsatzBandbreite = (typeof UMSATZ_BANDBREITE)[keyof typeof UMSATZ_BANDBREITE];
export const UMSATZ_BANDBREITE_VALUES: readonly UmsatzBandbreite[] = [
  UMSATZ_BANDBREITE.BIS_500K,
  UMSATZ_BANDBREITE.VON_500K_2M,
  UMSATZ_BANDBREITE.VON_2M_10M,
  UMSATZ_BANDBREITE.VON_10M_50M,
  UMSATZ_BANDBREITE.UEBER_50M,
];
export const UMSATZ_BANDBREITE_LABELS: Record<UmsatzBandbreite, string> = {
  [UMSATZ_BANDBREITE.BIS_500K]: 'bis CHF 500k',
  [UMSATZ_BANDBREITE.VON_500K_2M]: 'CHF 500k bis 2 Mio.',
  [UMSATZ_BANDBREITE.VON_2M_10M]: 'CHF 2 bis 10 Mio.',
  [UMSATZ_BANDBREITE.VON_10M_50M]: 'CHF 10 bis 50 Mio.',
  [UMSATZ_BANDBREITE.UEBER_50M]: 'ueber CHF 50 Mio.',
};

// ── Basissparten-Checkliste status vocab (Ch 4) ────────────────────────────────

/** The catalog Soll-status per Basissparte (the code-defined default, Ch 4). */
export const BASISSPARTEN_STATUS = {
  PFLICHT: 'pflicht',
  BEDINGT_PFLICHT: 'bedingt_pflicht',
  EMPFEHLUNG: 'empfehlung',
} as const;
export type BasisspartenStatus = (typeof BASISSPARTEN_STATUS)[keyof typeof BASISSPARTEN_STATUS];
export const BASISSPARTEN_STATUS_VALUES: readonly BasisspartenStatus[] = [
  BASISSPARTEN_STATUS.PFLICHT,
  BASISSPARTEN_STATUS.BEDINGT_PFLICHT,
  BASISSPARTEN_STATUS.EMPFEHLUNG,
];
export const BASISSPARTEN_STATUS_LABELS: Record<BasisspartenStatus, string> = {
  [BASISSPARTEN_STATUS.PFLICHT]: 'Pflicht',
  [BASISSPARTEN_STATUS.BEDINGT_PFLICHT]: 'Bedingt Pflicht',
  [BASISSPARTEN_STATUS.EMPFEHLUNG]: 'Empfehlung',
};

/** The broker's per-customer override of a Basissparte assessment (stored). */
export const BASISSPARTE_STATUS_OVERRIDE = {
  ABGEDECKT: 'abgedeckt',
  LUECKE: 'luecke',
  NICHT_RELEVANT: 'nicht_relevant',
} as const;
export type BasissparteStatusOverride =
  (typeof BASISSPARTE_STATUS_OVERRIDE)[keyof typeof BASISSPARTE_STATUS_OVERRIDE];
export const BASISSPARTE_STATUS_OVERRIDE_VALUES: readonly BasissparteStatusOverride[] = [
  BASISSPARTE_STATUS_OVERRIDE.ABGEDECKT,
  BASISSPARTE_STATUS_OVERRIDE.LUECKE,
  BASISSPARTE_STATUS_OVERRIDE.NICHT_RELEVANT,
];
export const BASISSPARTE_STATUS_OVERRIDE_LABELS: Record<BasissparteStatusOverride, string> = {
  [BASISSPARTE_STATUS_OVERRIDE.ABGEDECKT]: 'Abgedeckt',
  [BASISSPARTE_STATUS_OVERRIDE.LUECKE]: 'Deckungsluecke',
  [BASISSPARTE_STATUS_OVERRIDE.NICHT_RELEVANT]: 'Nicht relevant',
};

/** The Ampel colour shown per Basissparte row (derived — deriveBasisspartenAmpel). */
export const BASISSPARTEN_AMPEL = {
  GRUEN: 'gruen',
  GELB: 'gelb',
  ROT: 'rot',
  GRAU: 'grau',
} as const;
export type BasisspartenAmpel = (typeof BASISSPARTEN_AMPEL)[keyof typeof BASISSPARTEN_AMPEL];
export const BASISSPARTEN_AMPEL_VALUES: readonly BasisspartenAmpel[] = [
  BASISSPARTEN_AMPEL.GRUEN,
  BASISSPARTEN_AMPEL.GELB,
  BASISSPARTEN_AMPEL.ROT,
  BASISSPARTEN_AMPEL.GRAU,
];
export const BASISSPARTEN_AMPEL_LABELS: Record<BasisspartenAmpel, string> = {
  [BASISSPARTEN_AMPEL.GRUEN]: 'Abgedeckt',
  [BASISSPARTEN_AMPEL.GELB]: 'Zu pruefen',
  [BASISSPARTEN_AMPEL.ROT]: 'Pflicht offen / Luecke',
  [BASISSPARTEN_AMPEL.GRAU]: 'Nicht relevant',
};

// ── Shared reference constants ─────────────────────────────────────────────────

/** The 26 Swiss cantons (ISO-style codes). Used by wohnkanton + standortkantone. */
export const KANTON_CODES = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR', 'JU', 'LU', 'NE',
  'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG', 'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH',
] as const;
export type KantonCode = (typeof KANTON_CODES)[number];

/** The 9 IGB2B family-head codes (10000..90000) + German labels. */
export const IGB2B_FAMILIEN: Record<string, string> = {
  '10000': 'Fahrzeugversicherungen',
  '20000': 'Haftpflichtversicherungen',
  '30000': 'Unfallversicherungen',
  '40000': 'Krankenversicherungen',
  '50000': 'Transportversicherungen',
  '60000': 'Sachversicherungen',
  '70000': 'Technische Versicherungen',
  '80000': 'Versicherungen uebriger Branchen',
  '90000': 'Lebensversicherungen',
};

/**
 * The IGB2B family head of a branchencode = first digit x 10000 (e.g. '21000' -> '20000',
 * '93000' -> '90000'). P1 backfills some LOBs to their family head (haftpflicht -> 20000,
 * leben -> 90000, kranken -> 40000; see policy-write.core.ts), so a Basissparte on a leaf code
 * (21000/26000/93000/91000/41000, ...) must also match a policy carried under its family head.
 */
export function igb2bFamilyOf(code: string): string {
  return String(Math.floor(Number(code) / 10000) * 10000);
}

// ── Field catalog ──────────────────────────────────────────────────────────────

/** The input kind a Risikoprofil field renders as (RisikoprofilFeldInput dispatches on it). */
export const RISIKOPROFIL_FELD_TYP = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  SELECT: 'select',
  MULTISELECT: 'multiselect',
  // P2 Slice 2: an ISO date (yyyy-mm-dd) field — retroaktivdatum, Austrittsdatum, etc. The
  // Kunde catalog has no DATE field today; the Police branch catalog (police-risiko.ts) does.
  DATE: 'date',
} as const;
export type RisikoprofilFeldTyp =
  (typeof RISIKOPROFIL_FELD_TYP)[keyof typeof RISIKOPROFIL_FELD_TYP];

/**
 * The branchen-INDEPENDENT part of one atomic field — everything the input + validator need,
 * WITHOUT the Kunde-only `profil_typ`/`kategorie` grouping. P2 Slice 2 reuses this base for the
 * Police branch catalog (`BranchenFeldDefinition`), so `RisikoprofilFeldInput`,
 * `validateRisikoprofilAttributes` and the "x-of-y filled" counters work against either catalog.
 *
 * `key`        — snake-case, unique within a catalog slice (the json object key).
 * `label`      — user-visible German label.
 * `typ`        — the input kind (RisikoprofilFeldTyp).
 * `options`    — option values for select/multiselect (labels via risikoprofilOptionLabel).
 * `einheit`    — optional unit shown next to a number (e.g. 'CHF', '%').
 * `hinweis`    — optional helper line.
 */
export interface RisikoprofilFeldBasis {
  key: string;
  label: string;
  typ: RisikoprofilFeldTyp;
  options?: readonly string[];
  einheit?: string;
  hinweis?: string;
}

/**
 * One atomic STORED field in the Kunde `attributes_json`, adding the Kunde-level grouping:
 * `profil_typ` (which Risikoprofil-Typ the field belongs to) + `kategorie` (the card it sits on).
 */
export interface RisikoprofilFeldDefinition extends RisikoprofilFeldBasis {
  profil_typ: RisikoprofilTyp;
  kategorie: RisikoprofilKategorie;
}

const T = RISIKOPROFIL_TYP;
const K = RISIKOPROFIL_KATEGORIE;
const F = RISIKOPROFIL_FELD_TYP;

/**
 * The complete Risikoprofil field catalog: 38 Firma + 23 Privatperson = 61 STORED fields.
 * (The flat Schaden-je-5-Jahre fields left the catalog in P2 hardening Q15 — Schaden je Sparte
 * now lives per (Kunde, Branchencode) in customer_claims_history, read-only Altbestand aside.)
 * MIRROR (firma) and DERIVED (M4/M8) fields are NOT catalog entries (never in JSON); the
 * gated columns (gesundheitspruefung_status, einkommens_bandbreite) are NOT catalog entries
 * either. Atomization follows Ch 3: a "Ja/Nein + Kategorie" field is split into two keys.
 */
export const RISIKOPROFIL_FELD_KATALOG = [
  // ═══ FIRMA ═══════════════════════════════════════════════════════════════════
  // ── Unternehmensstruktur (9 stored; Rechtsform + Gruendungsjahr are MIRROR) ───
  { key: 'konzernrolle', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'Konzernrolle', typ: F.SELECT, options: ['konzern_mutter', 'konzern_tochter', 'konzern_schwester', 'konzern_eigenstaendig'] },
  { key: 'konzern_beteiligungsstruktur', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'Konzern- und Beteiligungsstruktur', typ: F.TEXTAREA },
  { key: 'konsolidierte_gesellschaften', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'Konsolidierte Gesellschaften (Anzahl)', typ: F.NUMBER },
  { key: 'betriebsstaetten_schweiz', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'Betriebsstaetten Schweiz (Anzahl)', typ: F.NUMBER },
  { key: 'betriebsstaetten_ausland', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'Betriebsstaetten Ausland (Anzahl)', typ: F.NUMBER },
  { key: 'exportanteil', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'Exportanteil', typ: F.SELECT, options: ['export_keiner', 'export_bis_25', 'export_25_50', 'export_50_75', 'export_ueber_75'] },
  { key: 'geschaeftslaender', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'Geschaeftslaender', typ: F.TEXTAREA },
  { key: 'mitversicherte_gesellschaften', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'Mitversicherte Gesellschaften', typ: F.TEXTAREA, hinweis: 'Fuer Konzernpolicen relevant.' },
  { key: 'usa_kanada_exposure', profil_typ: T.FIRMA, kategorie: K.UNTERNEHMENSSTRUKTUR, label: 'USA-/Kanada-Exposure', typ: F.BOOLEAN, hinweis: 'Haftpflichtrelevant.' },

  // ── Groesse & Finanzkennzahlen (5 stored; Mitarbeiter/Umsatz "bereits vorhanden") ─
  { key: 'mitarbeiteranzahl', profil_typ: T.FIRMA, kategorie: K.GROESSE_FINANZEN, label: 'Mitarbeiteranzahl', typ: F.NUMBER },
  { key: 'jahresumsatz_bandbreite', profil_typ: T.FIRMA, kategorie: K.GROESSE_FINANZEN, label: 'Jahresumsatz-Bandbreite', typ: F.SELECT, options: UMSATZ_BANDBREITE_VALUES },
  { key: 'bilanzsumme_bandbreite', profil_typ: T.FIRMA, kategorie: K.GROESSE_FINANZEN, label: 'Bilanzsumme-Bandbreite', typ: F.SELECT, options: UMSATZ_BANDBREITE_VALUES, hinweis: 'Aus Jahresrechnung.' },
  { key: 'eigenkapital_bandbreite', profil_typ: T.FIRMA, kategorie: K.GROESSE_FINANZEN, label: 'Eigenkapital-Bandbreite', typ: F.SELECT, options: UMSATZ_BANDBREITE_VALUES, hinweis: 'Aus Jahresrechnung.' },
  { key: 'lohnsumme_pa', profil_typ: T.FIRMA, kategorie: K.GROESSE_FINANZEN, label: 'Lohnsumme p.a.', typ: F.NUMBER, einheit: 'CHF', hinweis: 'Relevant fuer UVG, Kollektiv-Taggeld, BVG.' },

  // ── Branche & Geschaeftstaetigkeit (1 stored; NOGA + Zweck MIRROR, IGB2B DERIVED) ─
  { key: 'taetigkeitsfelder_haftungsrelevanz', profil_typ: T.FIRMA, kategorie: K.BRANCHE_TAETIGKEIT, label: 'Taetigkeitsfelder mit besonderer Haftungsrelevanz', typ: F.TEXTAREA, hinweis: 'z. B. Produkteexport, Beratung, Bauausfuehrung.' },

  // ── Betriebsrisiko-Indikatoren (8 stored; fuhrpark_vorhanden = Ch-4.1 trigger, Stefan-confirm) ─
  { key: 'produktions_lagerstaetten_vorhanden', profil_typ: T.FIRMA, kategorie: K.BETRIEBSRISIKO, label: 'Produktions-/Lagerstaetten vorhanden', typ: F.BOOLEAN },
  { key: 'produktions_lagerstaetten_kategorie', profil_typ: T.FIRMA, kategorie: K.BETRIEBSRISIKO, label: 'Produktions-/Lagerstaetten Kategorie', typ: F.SELECT, options: ['betrieb_produktion', 'betrieb_lager', 'betrieb_beides', 'betrieb_keine'] },
  { key: 'maschinenpark_kategorie', profil_typ: T.FIRMA, kategorie: K.BETRIEBSRISIKO, label: 'Maschinenpark-Kategorie', typ: F.SELECT, options: ['maschinen_keiner', 'maschinen_gering', 'maschinen_mittel', 'maschinen_umfangreich'] },
  { key: 'it_abhaengigkeit', profil_typ: T.FIRMA, kategorie: K.BETRIEBSRISIKO, label: 'IT-Abhaengigkeit / Digitalisierungsgrad', typ: F.SELECT, options: ['it_niedrig', 'it_mittel', 'it_hoch'], hinweis: 'Cyber-Exposure-Indikator.' },
  { key: 'einsatz_subunternehmer', profil_typ: T.FIRMA, kategorie: K.BETRIEBSRISIKO, label: 'Einsatz Subunternehmer/Personalverleih', typ: F.BOOLEAN },
  { key: 'gefahrstoffe_vorhanden', profil_typ: T.FIRMA, kategorie: K.BETRIEBSRISIKO, label: 'Umgang mit gefaehrlichen Stoffen/Prozessen', typ: F.BOOLEAN },
  { key: 'gefahrstoffe_kategorie', profil_typ: T.FIRMA, kategorie: K.BETRIEBSRISIKO, label: 'Gefahrstoffe/Prozesse Kategorie', typ: F.TEXT },
  { key: 'fuhrpark_vorhanden', profil_typ: T.FIRMA, kategorie: K.BETRIEBSRISIKO, label: 'Fuhrpark vorhanden', typ: F.BOOLEAN, hinweis: 'Trigger fuer MFZ-Haftpflicht (Basissparten); zur Bestaetigung mit dem Fachbereich.' },

  // ── Schaden- und Vertragshistorie (2 stored; Schaden je Sparte lives in customer_claims_history; Quote/Frequenz/Indizes DERIVED) ──
  { key: 'fruehere_kuendigung_vorversicherer', profil_typ: T.FIRMA, kategorie: K.SCHADEN_VERTRAGSHISTORIE, label: 'Fruehere Kuendigung durch Vorversicherer', typ: F.BOOLEAN },
  { key: 'kuendigungsgrund', profil_typ: T.FIRMA, kategorie: K.SCHADEN_VERTRAGSHISTORIE, label: 'Kuendigungsgrund', typ: F.TEXTAREA },

  // ── Sicherheits- & Praeventionsmassnahmen (10 stored) ─────────────────────────
  { key: 'brandschutz_sprinkleranlage', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Brandschutz: Sprinkleranlage', typ: F.BOOLEAN },
  { key: 'brandschutz_brandmeldeanlage', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Brandschutz: Brandmeldeanlage', typ: F.BOOLEAN },
  { key: 'einbruchschutz_alarmanlage', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Einbruchschutz: Alarmanlage', typ: F.BOOLEAN },
  { key: 'einbruchschutz_bewachung', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Einbruchschutz: Bewachung', typ: F.BOOLEAN },
  { key: 'cyber_firewall', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Cyber: Firewall', typ: F.BOOLEAN },
  { key: 'cyber_mfa', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Cyber: Multi-Faktor-Authentisierung', typ: F.BOOLEAN },
  { key: 'cyber_backup_konzept', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Cyber: Backup-Konzept', typ: F.BOOLEAN },
  { key: 'cyber_isms_zertifizierung', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Cyber: ISMS-Zertifizierung', typ: F.BOOLEAN },
  { key: 'cyber_isms_standard', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Cyber: ISMS-Standard', typ: F.TEXT, hinweis: 'z. B. ISO 27001.' },
  { key: 'business_continuity_plan', profil_typ: T.FIRMA, kategorie: K.SICHERHEIT_PRAEVENTION, label: 'Business-Continuity-/Notfallplan vorhanden', typ: F.BOOLEAN },

  // ── Geografisches Risiko (3 stored) ───────────────────────────────────────────
  { key: 'standortkantone', profil_typ: T.FIRMA, kategorie: K.GEOGRAFISCHES_RISIKO, label: 'Standortkanton(e)', typ: F.MULTISELECT, options: KANTON_CODES },
  { key: 'naturgefahrenzone', profil_typ: T.FIRMA, kategorie: K.GEOGRAFISCHES_RISIKO, label: 'Naturgefahrenzone', typ: F.MULTISELECT, options: ['natur_hochwasser', 'natur_erdbeben', 'natur_lawinen', 'natur_keine'] },
  { key: 'betriebsstaetten_exponierte_zonen', profil_typ: T.FIRMA, kategorie: K.GEOGRAFISCHES_RISIKO, label: 'Anzahl Betriebsstaetten in exponierten Zonen', typ: F.NUMBER },

  // (Portfolio- & Vertragskennzahlen: 0 stored — all DERIVED M4/M8, shown read-only.)

  // ═══ PRIVATPERSON ══════════════════════════════════════════════════════════════
  // ── Demografie & Haushalt (4 stored) ──────────────────────────────────────────
  { key: 'altersgruppe_bandbreite', profil_typ: T.PRIVATPERSON, kategorie: K.DEMOGRAFIE_HAUSHALT, label: 'Altersgruppe / Geburtsjahr-Bandbreite', typ: F.SELECT, options: ['alter_bis_25', 'alter_26_35', 'alter_36_45', 'alter_46_55', 'alter_56_65', 'alter_ueber_65'] },
  { key: 'zivilstand', profil_typ: T.PRIVATPERSON, kategorie: K.DEMOGRAFIE_HAUSHALT, label: 'Zivilstand', typ: F.SELECT, options: ['zivil_ledig', 'zivil_verheiratet', 'zivil_partnerschaft', 'zivil_geschieden', 'zivil_verwitwet'] },
  { key: 'haushaltsgroesse', profil_typ: T.PRIVATPERSON, kategorie: K.DEMOGRAFIE_HAUSHALT, label: 'Haushaltsgroesse', typ: F.NUMBER },
  { key: 'anzahl_kinder', profil_typ: T.PRIVATPERSON, kategorie: K.DEMOGRAFIE_HAUSHALT, label: 'Anzahl Kinder/unterhaltsberechtigte Personen', typ: F.NUMBER },

  // ── Wohnsituation (3 stored) ──────────────────────────────────────────────────
  { key: 'eigentum_miete', profil_typ: T.PRIVATPERSON, kategorie: K.WOHNSITUATION, label: 'Eigentum/Miete', typ: F.SELECT, options: ['wohn_eigentum', 'wohn_miete'] },
  { key: 'wohnobjekttyp', profil_typ: T.PRIVATPERSON, kategorie: K.WOHNSITUATION, label: 'Wohnobjekttyp', typ: F.SELECT, options: ['objekt_efh', 'objekt_eigentumswohnung', 'objekt_mietwohnung'] },
  { key: 'wohnkanton', profil_typ: T.PRIVATPERSON, kategorie: K.WOHNSITUATION, label: 'Wohnkanton / -region', typ: F.SELECT, options: KANTON_CODES },

  // ── Erwerbssituation (2 stored; Einkommens-Bandbreite is a GATED column) ───────
  { key: 'berufskategorie', profil_typ: T.PRIVATPERSON, kategorie: K.ERWERBSSITUATION, label: 'Berufskategorie/-gruppe', typ: F.TEXT, hinweis: 'Gefahrenklasse-relevant (Unfall).' },
  { key: 'erwerbsstatus', profil_typ: T.PRIVATPERSON, kategorie: K.ERWERBSSITUATION, label: 'Erwerbsstatus', typ: F.SELECT, options: ['erwerb_angestellt', 'erwerb_selbstaendig', 'erwerb_pensioniert', 'erwerb_ausbildung'] },

  // ── Vermoegens- und Objektsituation (5 stored) ────────────────────────────────
  { key: 'wohneigentum_vorhanden', profil_typ: T.PRIVATPERSON, kategorie: K.VERMOEGEN_OBJEKT, label: 'Wohneigentum vorhanden', typ: F.BOOLEAN },
  { key: 'fahrzeugbesitz_anzahl', profil_typ: T.PRIVATPERSON, kategorie: K.VERMOEGEN_OBJEKT, label: 'Fahrzeugbesitz (Anzahl)', typ: F.NUMBER },
  { key: 'fahrzeugbesitz_typ', profil_typ: T.PRIVATPERSON, kategorie: K.VERMOEGEN_OBJEKT, label: 'Fahrzeugbesitz (Typ)', typ: F.TEXT },
  { key: 'wertgegenstaende_vorhanden', profil_typ: T.PRIVATPERSON, kategorie: K.VERMOEGEN_OBJEKT, label: 'Wertgegenstaende/Kunstbesitz vorhanden', typ: F.BOOLEAN },
  { key: 'wertgegenstaende_kategorie', profil_typ: T.PRIVATPERSON, kategorie: K.VERMOEGEN_OBJEKT, label: 'Wertgegenstaende Kategorie', typ: F.SELECT, options: ['wert_schmuck', 'wert_kunst', 'wert_sammlungen', 'wert_andere'] },

  // ── Risikoaffinitaet & Lebensstil (6 stored) ──────────────────────────────────
  { key: 'extremsport_vorhanden', profil_typ: T.PRIVATPERSON, kategorie: K.RISIKOAFFINITAET_LEBENSSTIL, label: 'Risiko-/Extremsportarten', typ: F.BOOLEAN },
  { key: 'extremsport_art', profil_typ: T.PRIVATPERSON, kategorie: K.RISIKOAFFINITAET_LEBENSSTIL, label: 'Art der Risiko-/Extremsportart', typ: F.TEXT },
  { key: 'reisehaeufigkeit', profil_typ: T.PRIVATPERSON, kategorie: K.RISIKOAFFINITAET_LEBENSSTIL, label: 'Reisehaeufigkeit', typ: F.SELECT, options: ['reise_selten', 'reise_gelegentlich', 'reise_haeufig'] },
  { key: 'typische_reiseziele', profil_typ: T.PRIVATPERSON, kategorie: K.RISIKOAFFINITAET_LEBENSSTIL, label: 'Typische Reiseziele', typ: F.TEXTAREA },
  { key: 'haustier_vorhanden', profil_typ: T.PRIVATPERSON, kategorie: K.RISIKOAFFINITAET_LEBENSSTIL, label: 'Haustierhaltung', typ: F.BOOLEAN },
  { key: 'hunderasse', profil_typ: T.PRIVATPERSON, kategorie: K.RISIKOAFFINITAET_LEBENSSTIL, label: 'Hunderasse', typ: F.TEXT, hinweis: 'Kantonal teils obligatorische Hundehaftpflicht.' },

  // ── Vorsorgesituation (2 stored) ──────────────────────────────────────────────
  { key: 'saeule_3a_vorhanden', profil_typ: T.PRIVATPERSON, kategorie: K.VORSORGE, label: 'Saeule-3a-/Vorsorgeloesung vorhanden', typ: F.BOOLEAN },
  { key: 'vorsorgeausweis_vorhanden', profil_typ: T.PRIVATPERSON, kategorie: K.VORSORGE, label: 'Vorsorgeausweis mit Deckungsgrad vorhanden', typ: F.BOOLEAN },

  // ── Schaden- und Vertragshistorie (1 stored; Schaden je Sparte lives in customer_claims_history; Deckungsluecken DERIVED) ──
  { key: 'fruehere_kuendigung_vorversicherer', profil_typ: T.PRIVATPERSON, kategorie: K.SCHADEN_VERTRAGSHISTORIE, label: 'Fruehere Kuendigung durch Vorversicherer', typ: F.BOOLEAN },

  // (Besondere Personendaten: 0 stored — the two gated columns + einwilligung are rendered
  //  from the typed spine / consent, never from attributes_json.)
] as const satisfies readonly RisikoprofilFeldDefinition[];

/** The two gated field keys — DEDICATED columns on customer_risk_profile, never in JSON. */
export const RISIKOPROFIL_GATED_FIELD_KEYS = [
  'gesundheitspruefung_status',
  'einkommens_bandbreite',
] as const;
export type RisikoprofilGatedFieldKey = (typeof RISIKOPROFIL_GATED_FIELD_KEYS)[number];

/** The free-text consent purpose used for the besondere Personendaten (revDSG Art. 5 lit. c). */
export const RISIKOPROFIL_CONSENT_PURPOSE = 'risikoprofil_besondere_personendaten' as const;

/** Flat option-value → German label map (bands + per-field selects). Kantone self-label. */
export const RISIKOPROFIL_OPTIONEN_LABELS: Record<string, string> = {
  ...UMSATZ_BANDBREITE_LABELS,
  ...EINKOMMENS_BANDBREITE_LABELS,
  ...GESUNDHEITSPRUEFUNG_STATUS_LABELS,
  // Firma selects
  konzern_mutter: 'Muttergesellschaft',
  konzern_tochter: 'Tochtergesellschaft',
  konzern_schwester: 'Schwestergesellschaft',
  konzern_eigenstaendig: 'Eigenstaendig',
  export_keiner: 'Kein Export',
  export_bis_25: 'bis 25 %',
  export_25_50: '25 bis 50 %',
  export_50_75: '50 bis 75 %',
  export_ueber_75: 'ueber 75 %',
  betrieb_produktion: 'Produktion',
  betrieb_lager: 'Lager',
  betrieb_beides: 'Produktion und Lager',
  betrieb_keine: 'Keine',
  maschinen_keiner: 'Kein Maschinenpark',
  maschinen_gering: 'Gering',
  maschinen_mittel: 'Mittel',
  maschinen_umfangreich: 'Umfangreich',
  it_niedrig: 'Niedrig',
  it_mittel: 'Mittel',
  it_hoch: 'Hoch',
  natur_hochwasser: 'Hochwasser',
  natur_erdbeben: 'Erdbeben',
  natur_lawinen: 'Lawinen',
  natur_keine: 'Keine',
  // Privatperson selects
  alter_bis_25: 'bis 25 Jahre',
  alter_26_35: '26 bis 35 Jahre',
  alter_36_45: '36 bis 45 Jahre',
  alter_46_55: '46 bis 55 Jahre',
  alter_56_65: '56 bis 65 Jahre',
  alter_ueber_65: 'ueber 65 Jahre',
  zivil_ledig: 'Ledig',
  zivil_verheiratet: 'Verheiratet',
  zivil_partnerschaft: 'Eingetragene Partnerschaft',
  zivil_geschieden: 'Geschieden',
  zivil_verwitwet: 'Verwitwet',
  wohn_eigentum: 'Eigentum',
  wohn_miete: 'Miete',
  objekt_efh: 'Einfamilienhaus',
  objekt_eigentumswohnung: 'Eigentumswohnung',
  objekt_mietwohnung: 'Mietwohnung',
  erwerb_angestellt: 'Angestellt',
  erwerb_selbstaendig: 'Selbstaendig',
  erwerb_pensioniert: 'Pensioniert',
  erwerb_ausbildung: 'In Ausbildung',
  wert_schmuck: 'Schmuck',
  wert_kunst: 'Kunst',
  wert_sammlungen: 'Sammlungen',
  wert_andere: 'Andere',
  reise_selten: 'Selten',
  reise_gelegentlich: 'Gelegentlich',
  reise_haeufig: 'Haeufig',
};

/** German label for an option value (Kanton codes label as themselves; unknown = value). */
export function risikoprofilOptionLabel(value: string): string {
  if ((KANTON_CODES as readonly string[]).includes(value)) return value;
  return RISIKOPROFIL_OPTIONEN_LABELS[value] ?? value;
}

// ── Basissparten-Checkliste (the branchenunabhaengige Soll, Ch 4) ──────────────

/**
 * One Basissparte in the code-defined Checkliste (the Soll). `sparte_key` is the stable
 * per-Typ slug (the baseline row key); `branchencode` is the IGB2B code (same code may
 * repeat across rows, e.g. 60000 for Gebaeude and Hausrat).
 */
export interface BasisspartenDefinition {
  sparte_key: string;
  profil_typ: RisikoprofilTyp;
  branchencode: string;
  label: string;
  default_status: BasisspartenStatus;
  kriterium: string;
  richtwert: string;
}

/** 9 Firma + 9 Privatperson = 18 rows (Ch 4.1 / 4.2). Codes verified against InsuranceType.csv. */
export const BASISSPARTEN_CHECKLISTE = [
  // ── Firmenkunden (Ch 4.1) ─────────────────────────────────────────────────────
  { sparte_key: 'firma_uvg', profil_typ: T.FIRMA, branchencode: '38000', label: 'Obligatorische Unfallversicherung UVG', default_status: BASISSPARTEN_STATUS.PFLICHT, kriterium: 'Pflicht ab 1 Mitarbeitendem.', richtwert: 'Berufsunfall ab 1. Arbeitsstunde, Nichtberufsunfall ab 8 Std./Woche (Art. 1a UVG).' },
  { sparte_key: 'firma_bvg', profil_typ: T.FIRMA, branchencode: '93000', label: 'Kollektivleben BVG', default_status: BASISSPARTEN_STATUS.PFLICHT, kriterium: 'Pflicht ab BVG-Eintrittsschwelle.', richtwert: 'Koordinierter Lohn gemaess gesetzlichen Mindestvorgaben.' },
  { sparte_key: 'firma_betriebshaftpflicht', profil_typ: T.FIRMA, branchencode: '21000', label: 'Betriebshaftpflicht', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung (Marktstandard).', richtwert: 'Richtwert CHF 2 bis 5 Mio., hoeher bei erhoehtem Risiko.' },
  { sparte_key: 'firma_sach_gebaeude_inventar', profil_typ: T.FIRMA, branchencode: '60000', label: 'Sachversicherung Gebaeude/Inventar', default_status: BASISSPARTEN_STATUS.BEDINGT_PFLICHT, kriterium: 'Gebaeude: Pflicht in Monopol-Kantonen; Inventar: Empfehlung.', richtwert: 'Neuwertbasis.' },
  { sparte_key: 'firma_rechtsschutz', profil_typ: T.FIRMA, branchencode: '82000', label: 'Rechtsschutz Betrieb', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung.', richtwert: 'Je nach Taetigkeitsfeld.' },
  { sparte_key: 'firma_cyber', profil_typ: T.FIRMA, branchencode: '87000', label: 'Cyberversicherung', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung, wachsend Richtung Standard.', richtwert: 'Bei IT-Abhaengigkeit mittel/hoch.' },
  { sparte_key: 'firma_do', profil_typ: T.FIRMA, branchencode: '24000', label: 'Organhaftpflicht D&O', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung bei Kapitalgesellschaften.', richtwert: 'Nach Bilanzsumme/Kotierungsstatus.' },
  { sparte_key: 'firma_krankentaggeld', profil_typ: T.FIRMA, branchencode: '42000', label: 'Kollektiv-Krankentaggeld', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung.', richtwert: 'Lohnsumme (OR Art. 324a).' },
  { sparte_key: 'firma_mfz_haftpflicht', profil_typ: T.FIRMA, branchencode: '11000', label: 'Motorfahrzeug-Haftpflicht', default_status: BASISSPARTEN_STATUS.BEDINGT_PFLICHT, kriterium: 'Pflicht, falls Fuhrpark vorhanden.', richtwert: 'Gesetzliches Minimum SVG Art. 63.' },

  // ── Privatpersonen (Ch 4.2) ───────────────────────────────────────────────────
  { sparte_key: 'privat_kvg', profil_typ: T.PRIVATPERSON, branchencode: '41000', label: 'Krankenversicherung KVG', default_status: BASISSPARTEN_STATUS.PFLICHT, kriterium: 'Pflicht fuer alle CH-Wohnsitzpersonen.', richtwert: 'Gesetzliches Obligatorium.' },
  { sparte_key: 'privat_haftpflicht', profil_typ: T.PRIVATPERSON, branchencode: '26000', label: 'Privathaftpflicht', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung; Hundehalterhaftpflicht kantonal teils Pflicht.', richtwert: 'CHF 5 bis 10 Mio.' },
  { sparte_key: 'privat_hausrat', profil_typ: T.PRIVATPERSON, branchencode: '60000', label: 'Hausratversicherung', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung.', richtwert: 'Neuwertbasis.' },
  { sparte_key: 'privat_gebaeude', profil_typ: T.PRIVATPERSON, branchencode: '60000', label: 'Gebaeudeversicherung', default_status: BASISSPARTEN_STATUS.BEDINGT_PFLICHT, kriterium: 'Pflicht in Monopol-Kantonen; sonst Empfehlung.', richtwert: 'Neuwertbasis.' },
  { sparte_key: 'privat_mfz_haftpflicht', profil_typ: T.PRIVATPERSON, branchencode: '11000', label: 'Motorfahrzeug-Haftpflicht', default_status: BASISSPARTEN_STATUS.BEDINGT_PFLICHT, kriterium: 'Pflicht, falls Fahrzeug gehalten.', richtwert: 'SVG Art. 63 Minimum.' },
  { sparte_key: 'privat_rechtsschutz', profil_typ: T.PRIVATPERSON, branchencode: '82000', label: 'Rechtsschutz Privat', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung.', richtwert: 'Nach Bedarf.' },
  { sparte_key: 'privat_vorsorge', profil_typ: T.PRIVATPERSON, branchencode: '91000', label: 'Todesfall-/EU-Vorsorge', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung bei Unterhaltspflicht/Hypothek.', richtwert: 'Nach Bedarf.' },
  { sparte_key: 'privat_einzelunfall', profil_typ: T.PRIVATPERSON, branchencode: '31000', label: 'Einzelunfallversicherung', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung bei UVG-Luecke.', richtwert: 'Nach individuellem Bedarf.' },
  { sparte_key: 'privat_reise', profil_typ: T.PRIVATPERSON, branchencode: '83500', label: 'Reiseversicherung/Assistance', default_status: BASISSPARTEN_STATUS.EMPFEHLUNG, kriterium: 'Empfehlung bei Reiseaktivitaet.', richtwert: 'Nach Bedarf.' },
] as const satisfies readonly BasisspartenDefinition[];

// ── Domain events (emitted through the activity/audit + pg-boss mechanism) ─────

export const RISIKOPROFIL_EVENTS = {
  RISK_PROFILE_UPDATED: 'customer.risk_profile_updated',
  COVERAGE_BASELINE_UPDATED: 'customer.coverage_baseline_updated',
  CLAIMS_HISTORY_UPDATED: 'customer.claims_history_updated',
} as const;
export type RisikoprofilEvent = (typeof RISIKOPROFIL_EVENTS)[keyof typeof RISIKOPROFIL_EVENTS];

// ── Pure helpers ────────────────────────────────────────────────────────────────

/** Map a customer subject_type to its Risikoprofil-Typ (person -> privatperson). */
export function risikoprofilTypFuerSubject(subjectType: SubjectKind | string): RisikoprofilTyp {
  return subjectType === 'firma' ? RISIKOPROFIL_TYP.FIRMA : RISIKOPROFIL_TYP.PRIVATPERSON;
}

/** The STORED field definitions for one Typ (catalog slice, in catalog order). */
export function risikoprofilFelderFuer(typ: RisikoprofilTyp): readonly RisikoprofilFeldDefinition[] {
  return RISIKOPROFIL_FELD_KATALOG.filter((f) => f.profil_typ === typ);
}

/** The Basissparten rows for one Typ (in catalog order). */
export function basisspartenFuer(typ: RisikoprofilTyp): readonly BasisspartenDefinition[] {
  return BASISSPARTEN_CHECKLISTE.filter((b) => b.profil_typ === typ);
}

/** One validation error against the field catalog. */
export interface RisikoprofilValidationError {
  key: string;
  message: string;
}

/**
 * Validate a merge-patch of attribute keys/values against a field catalog (default: the Kunde
 * catalog; Slice 2 passes its branch catalog). Returns the list of errors (empty = valid).
 *
 * Rules: every key must exist in the catalog; a `null` value clears a field (always allowed);
 * the value type must match the field typ (number/boolean/string); a select value must be one of
 * the field's options; a multiselect value must be a string[] subset of the options. The
 * catalog is the only source of truth, so unknown keys (incl. the gated-column keys, which are
 * never in `attributes_json`) are rejected.
 */
export function validateRisikoprofilAttributes(
  attributes: Record<string, unknown>,
  katalog: readonly Pick<RisikoprofilFeldBasis, 'key' | 'typ' | 'options'>[] = RISIKOPROFIL_FELD_KATALOG,
): RisikoprofilValidationError[] {
  const errors: RisikoprofilValidationError[] = [];
  const byKey = new Map(katalog.map((f) => [f.key, f]));

  for (const [key, value] of Object.entries(attributes)) {
    const def = byKey.get(key);
    if (!def) {
      errors.push({ key, message: `Unbekanntes Feld "${key}".` });
      continue;
    }
    if (value === null) continue; // clearing a field is always allowed

    switch (def.typ) {
      case RISIKOPROFIL_FELD_TYP.NUMBER:
        if (typeof value !== 'number' || Number.isNaN(value)) {
          errors.push({ key, message: 'Erwartet eine Zahl.' });
        } else if (!Number.isFinite(value) || Math.abs(value) > RISIKOPROFIL_NUMBER_MAX) {
          errors.push({ key, message: 'Zahl ausserhalb des zulaessigen Bereichs.' });
        }
        break;
      case RISIKOPROFIL_FELD_TYP.BOOLEAN:
        if (typeof value !== 'boolean') {
          errors.push({ key, message: 'Erwartet einen Wahrheitswert.' });
        }
        break;
      case RISIKOPROFIL_FELD_TYP.TEXT:
        if (typeof value !== 'string') {
          errors.push({ key, message: 'Erwartet einen Text.' });
        } else if (value.length > RISIKOPROFIL_TEXT_MAX) {
          errors.push({ key, message: `Text zu lang (max. ${RISIKOPROFIL_TEXT_MAX} Zeichen).` });
        }
        break;
      case RISIKOPROFIL_FELD_TYP.TEXTAREA:
        if (typeof value !== 'string') {
          errors.push({ key, message: 'Erwartet einen Text.' });
        } else if (value.length > RISIKOPROFIL_TEXTAREA_MAX) {
          errors.push({ key, message: `Text zu lang (max. ${RISIKOPROFIL_TEXTAREA_MAX} Zeichen).` });
        }
        break;
      case RISIKOPROFIL_FELD_TYP.DATE:
        if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          errors.push({ key, message: 'Erwartet ein Datum (JJJJ-MM-TT).' });
        }
        break;
      case RISIKOPROFIL_FELD_TYP.SELECT:
        if (typeof value !== 'string' || !(def.options ?? []).includes(value)) {
          errors.push({ key, message: 'Unzulaessiger Auswahlwert.' });
        }
        break;
      case RISIKOPROFIL_FELD_TYP.MULTISELECT:
        if (
          !Array.isArray(value) ||
          value.some((v) => typeof v !== 'string' || !(def.options ?? []).includes(v))
        ) {
          errors.push({ key, message: 'Unzulaessige Mehrfachauswahl.' });
        } else if (new Set(value).size !== value.length) {
          errors.push({ key, message: 'Doppelte Mehrfachauswahl.' });
        }
        break;
      default:
        errors.push({ key, message: 'Unbekannter Feldtyp.' });
    }
  }
  return errors;
}

/**
 * Is a STORED attribute value "filled" (counts toward the quality score / x-of-y)?
 * Exported (P2 Slice 2) so the Police branch catalog counters reuse the EXACT same rule:
 * null/undefined/'' (blank string) and [] are empty; numbers (incl. 0) and booleans (incl.
 * false) count as answered.
 */
export function isRisikoprofilWertGefuellt(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true; // numbers (incl. 0) and booleans (incl. false) count as answered
}

/**
 * "x of y filled" over an arbitrary catalog slice + an attributes record. The ONE counting
 * primitive both the Kunde quality score and the Police branch "x/N Sparte gespeichert" build on
 * (P2 Slice 2). Pure; takes the field list by key so it is catalog-agnostic.
 */
export function countFilledFelder(
  felder: readonly Pick<RisikoprofilFeldBasis, 'key'>[],
  attributes: Record<string, unknown>,
): { filled: number; total: number } {
  const filled = felder.filter((f) => isRisikoprofilWertGefuellt(attributes[f.key])).length;
  return { filled, total: felder.length };
}

/**
 * Data-quality score (0..100, two decimals) = share of the Typ's STORED catalog fields that
 * carry a filled value in `attributes`. The two gated columns are NOT part of the stored
 * catalog, so they never move the score (they live on the typed spine).
 */
export function computeDataQualityScore(
  typ: RisikoprofilTyp,
  attributes: Record<string, unknown>,
): number {
  const { filled, total } = countFilledFelder(risikoprofilFelderFuer(typ), attributes);
  if (total === 0) return 0;
  return Math.round((filled / total) * 10000) / 100;
}

/**
 * Data-quality score (0..100, two decimals) over an ARBITRARY resolved field list (P2 Slice 3).
 * The resolver-driven sibling of computeDataQualityScore: the Kunde service scores against the
 * RESOLVED felder (DB schema if present, else code catalog) instead of the hard-coded Typ slice.
 */
export function dataQualityScoreFuerFelder(
  felder: readonly Pick<RisikoprofilFeldBasis, 'key'>[],
  attributes: Record<string, unknown>,
): number {
  const { filled, total } = countFilledFelder(felder, attributes);
  if (total === 0) return 0;
  return Math.round((filled / total) * 10000) / 100;
}

/** "x von y gespeichert" counts for one Kategorie card. */
export function countFilledInKategorie(
  typ: RisikoprofilTyp,
  kategorie: RisikoprofilKategorie,
  attributes: Record<string, unknown>,
): { filled: number; total: number } {
  const felder = risikoprofilFelderFuer(typ).filter((f) => f.kategorie === kategorie);
  return countFilledFelder(felder, attributes);
}

/**
 * Derive the Ampel colour of a Basissparte row from the broker's per-customer assessment AND
 * whether a matching active policy exists (Ausschreibung Ch 7.1). Precedence:
 *   status_override 'nicht_relevant'  -> GRAU (broker explicitly excluded it)
 *   applicable === false              -> GRAU (nicht relevant)
 *   hasPolicy === true                -> GRUEN (Police vorhanden; a held cover is NEVER a rote Luecke)
 *   status_override 'abgedeckt'       -> GRUEN
 *   status_override 'luecke'          -> ROT  (broker flags a gap, no matching police)
 *   applicable === true               -> GELB (relevant ohne Police = zu pruefen)
 *   default 'pflicht' (unassessed)    -> ROT  (unbestaetigte Pflichtdeckung ohne Police = Handlungsbedarf)
 *   otherwise                         -> GELB (neutral / zu pruefen)
 *
 * Pure function. `hasPolicy` is resolved api-side via the family-aware policy match
 * (exact branchencode OR the policy's family head == igb2bFamilyOf(branchencode)).
 */
export function deriveBasisspartenAmpel(input: {
  default_status: BasisspartenStatus;
  applicable: boolean | null;
  status_override: BasissparteStatusOverride | null;
  hasPolicy: boolean;
}): BasisspartenAmpel {
  if (input.status_override === BASISSPARTE_STATUS_OVERRIDE.NICHT_RELEVANT) return BASISSPARTEN_AMPEL.GRAU;
  if (input.applicable === false) return BASISSPARTEN_AMPEL.GRAU;
  if (input.hasPolicy) return BASISSPARTEN_AMPEL.GRUEN;
  if (input.status_override === BASISSPARTE_STATUS_OVERRIDE.ABGEDECKT) return BASISSPARTEN_AMPEL.GRUEN;
  if (input.status_override === BASISSPARTE_STATUS_OVERRIDE.LUECKE) return BASISSPARTEN_AMPEL.ROT;
  if (input.applicable === true) return BASISSPARTEN_AMPEL.GELB;
  if (input.default_status === BASISSPARTEN_STATUS.PFLICHT) return BASISSPARTEN_AMPEL.ROT;
  return BASISSPARTEN_AMPEL.GELB;
}

// ── Wire types (snake_case) ──────────────────────────────────────────────────────

/** One mirrored, read-only Stammdaten value from `firma` (Ch 3.1 "bereits vorhanden"/Zefix). */
export interface RisikoprofilStammdatenMirror {
  rechtsform: string | null;
  gruendungsjahr: number | null;
  noga_code: string | null;
  zweckbeschreibung: string | null;
}

/** Read-only derived values (M4 counts now; M8 KPIs typed-null until M8 lands). */
export interface RisikoprofilDerived {
  anzahl_aktiver_policen: number | null;
  anzahl_abgedeckter_sparten: number | null;
  igb2b_hauptbranchen: string[];
  // M8 KPIs (typed-null placeholders until the Portfolio-KPI module feeds them):
  schadenquote: number | null;
  schadenfrequenz: number | null;
  gesamtpraemienvolumen: number | null;
  deckungsluecken_index: number | null;
  underinsurance_score: number | null;
  praemienkonzentration_groesste_police: number | null;
  mutationsrate_renewal_historie: number | null;
}

/** The besondere-Personendaten consent state (derived from the consent table). */
export interface RisikoprofilEinwilligung {
  erfasst: boolean;
  datum: string | null;
}

/** GET .../risk-profile response. Gated VALUES are stripped (not just nulled) when suppressed. */
export interface RisikoprofilResponse {
  subject_type: 'person' | 'firma';
  subject_id: string;
  profile_type: RisikoprofilTyp;
  attributes: Record<string, unknown>;
  /**
   * The RESOLVED Risikoprofil-field catalog for this Typ (P2 Slice 3): the DB schema if the tenant
   * has any `ebene='kunde'` rows, else the code catalog. Optional — pre-Slice-3 callers omit it and
   * the web falls back to `risikoprofilFelderFuer(profile_type)`.
   */
  felder?: RisikoprofilFeldDefinition[];
  /** Gated columns — present only when the caller may see them; otherwise omitted. */
  gesundheitspruefung_status?: GesundheitspruefungStatus | null;
  einkommens_bandbreite?: EinkommensBandbreite | null;
  /** Gated field keys that were suppressed on this read (role-hidden and/or consent-missing). */
  gated_fields_suppressed: string[];
  stammdaten_mirror: RisikoprofilStammdatenMirror | null;
  derived: RisikoprofilDerived;
  einwilligung: RisikoprofilEinwilligung;
  data_quality_score: number | null;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  updated_at: string;
}

/**
 * PATCH .../risk-profile body — a merge-patch of `attributes` plus the two gated columns.
 * `expected_updated_at` is the optimistic-concurrency token (mismatch -> 409). A `null` value
 * inside `attributes` clears that single field.
 */
export interface RisikoprofilPatchBody {
  attributes?: Record<string, unknown>;
  gesundheitspruefung_status?: GesundheitspruefungStatus | null;
  einkommens_bandbreite?: EinkommensBandbreite | null;
  expected_updated_at?: string;
}

/** PUT .../risk-profile/einwilligung body — records/withdraws the besondere-Personendaten consent. */
export interface RisikoprofilEinwilligungBody {
  erfasst: boolean;
}

/** GET .../coverage-baseline item (one Basissparte with its per-customer assessment). */
export interface BasisspartenItem {
  sparte_key: string;
  branchencode: string;
  label: string;
  default_status: BasisspartenStatus;
  kriterium: string;
  richtwert: string;
  applicable: boolean | null;
  status_override: BasissparteStatusOverride | null;
  notiz: string | null;
  ampel: BasisspartenAmpel;
  /** Policies of this customer in this branchencode (value link resolved api-side). */
  policy_ids: string[];
}

export interface BasisspartenResponse {
  subject_type: 'person' | 'firma';
  subject_id: string;
  profile_type: RisikoprofilTyp;
  items: BasisspartenItem[];
}

/** PUT .../coverage-baseline/:sparteKey body — the broker's per-customer assessment of one Sparte. */
export interface BasisspartePutBody {
  applicable?: boolean | null;
  status_override?: BasissparteStatusOverride | null;
  notiz?: string | null;
}

// ── Schadenverlauf je Sparte (P2 hardening Q15) ─────────────────────────────────

/** The two retired flat catalog keys; values may still sit in attributes_json (Altbestand). */
export const RISIKOPROFIL_LEGACY_SCHADEN_KEYS = ['anzahl_schaeden_5j', 'art_schaeden_5j'] as const;

export const KUNDE_SCHADEN_QUELLE = { MANUELL: 'manuell', SCHADENAUSKUNFT: 'schadenauskunft' } as const;
export type KundeSchadenQuelle = (typeof KUNDE_SCHADEN_QUELLE)[keyof typeof KUNDE_SCHADEN_QUELLE];
export const KUNDE_SCHADEN_QUELLE_VALUES: readonly KundeSchadenQuelle[] = ['manuell', 'schadenauskunft'];
export const KUNDE_SCHADEN_QUELLE_LABELS: Record<KundeSchadenQuelle, string> = {
  manuell: 'Manuell erfasst',
  schadenauskunft: 'Aus Schadenauskunft uebernommen',
};

/** Suggestion list for the free-text «Art der Schaeden» (datalist; stored value stays free text). */
export const SCHADEN_ART_VORSCHLAEGE: readonly string[] = [
  'Sachschaden', 'Haftpflichtschaden', 'Personenschaden', 'Diebstahl/Einbruch', 'Elementarschaden',
  'Feuer', 'Wasser', 'Glas', 'Fahrzeugkollision', 'Cyber-Vorfall', 'Rechtsschutzfall', 'Sonstiges',
];

/** Bounds mirrored from the 0158 CHECKs (the DTO validates with them). */
export const KUNDE_SCHADEN_ANZAHL_MAX = 9999;
export const KUNDE_SCHADEN_TEXT_MAX = 2000;
/** schadensumme is numeric(14,2) → the largest value that fits is 999'999'999'999.99 (12 integer digits). */
export const KUNDE_SCHADEN_SUMME_MAX = 999999999999.99;

// ── Size caps for Risikoprofil attribute values (PR #43 review #13) ─────────────
/** Max length of a TEXT (single-line) attribute value. */
export const RISIKOPROFIL_TEXT_MAX = 200;
/** Max length of a TEXTAREA attribute value (house number for free text; = KUNDE_SCHADEN_TEXT_MAX). */
export const RISIKOPROFIL_TEXTAREA_MAX = 5000;
/** Max |value| of a NUMBER attribute. Mirrors numeric(14,2) so a value can always be promoted to a numeric column. */
export const RISIKOPROFIL_NUMBER_MAX = 999999999999.99;
/** Max length of a free Notiz on a Basissparte / Deckung row (app-level; no DB CHECK). */
export const RISIKOPROFIL_NOTIZ_MAX = 2000;

/** One IGB2B Sparte option for the picker (label from igb2b_kategorie, grouped by familie). */
export interface SparteOption {
  branchencode: string;
  label: string;
  familie: string;
  familie_label: string;
}

/** The read-only Schadenauskunft hint for one Sparte (aggregated over the Kunde's policies' CURRENT entries). */
export interface KundeSchadenAuskunftHinweis {
  anzahl: number;
  summe: number | null;
  policen: number;
  retrieved_at: string;
}

/** One stored per-Sparte row on the wire. */
export interface KundeSchadenSparteRow {
  id: string;
  branchencode: string;
  anzahl_schaeden: number;
  art_schaeden: string | null;
  schadensumme: number | null;
  zeitraum_von: string;
  zeitraum_bis: string;
  quelle: KundeSchadenQuelle;
  notiz: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  updated_at: string;
}

/** One list item: a Sparte that has a stored row and/or an Auskunft hint. */
export interface KundeSchadenSparteItem {
  branchencode: string;
  label: string;
  familie: string;
  row: KundeSchadenSparteRow | null;
  auskunft: KundeSchadenAuskunftHinweis | null;
}

/** The retired flat values still present in attributes_json (null when none). */
export interface KundeSchadenLegacyAggregate {
  anzahl_schaeden_5j: number | null;
  art_schaeden_5j: string | null;
}

/** GET .../claims-history response. */
export interface KundeSchadenverlaufResponse {
  subject_type: 'person' | 'firma';
  subject_id: string;
  profile_type: RisikoprofilTyp;
  zeitraum_default: { von: string; bis: string };
  /** Sorted by familie, then branchencode. */
  items: KundeSchadenSparteItem[];
  legacy_aggregate: KundeSchadenLegacyAggregate | null;
  /** All 59 codes with labels, for the picker. */
  sparten_katalog: SparteOption[];
}

/** PUT .../claims-history/:branchencode body (true merge; explicit null clears; absent keeps). */
export interface KundeSchadenSpartePutBody {
  anzahl_schaeden?: number; // required on first write (400 otherwise)
  art_schaeden?: string | null;
  schadensumme?: number | null;
  zeitraum_von?: string; // ISO date; default rolling 5y on insert
  zeitraum_bis?: string;
  quelle?: KundeSchadenQuelle; // default 'manuell' on insert; absent keeps
  notiz?: string | null;
  expected_updated_at?: string; // 409 on mismatch (existing row only)
}

/** Pure: read the legacy aggregate out of attributes_json (null when both keys absent/empty). */
export function legacySchadenAggregateAus(
  attributes: Record<string, unknown>,
): KundeSchadenLegacyAggregate | null {
  const anzahlRaw = attributes['anzahl_schaeden_5j'];
  const artRaw = attributes['art_schaeden_5j'];
  const hasAnzahl = isRisikoprofilWertGefuellt(anzahlRaw);
  const hasArt = isRisikoprofilWertGefuellt(artRaw);
  if (!hasAnzahl && !hasArt) return null;
  return {
    anzahl_schaeden_5j: hasAnzahl && typeof anzahlRaw === 'number' ? anzahlRaw : null,
    art_schaeden_5j: hasArt && typeof artRaw === 'string' ? artRaw : null,
  };
}

/** Pure: 'Fahrzeugversicherungen' etc. for a code via igb2bFamilyOf + IGB2B_FAMILIEN ('' when unknown). */
export function igb2bFamilieLabel(code: string): string {
  return IGB2B_FAMILIEN[igb2bFamilyOf(code)] ?? '';
}
